"use client";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@libs/axios";
import { notifications } from "@mantine/notifications";

interface SyncResponResponse {
  nomorAju?: string;
  dataResponCount?: number;
  dataStatusCount?: number;
  savedCount?: number;
  statusSavedCount?: number;
  emailsSent?: number;
  emailRecipients?: string[];
  latestRespon?: string;
  latestResponDate?: string;
  errors?: string[];
  message?: string;
}

interface ApiEnvelope {
  success?: boolean;
  message?: string;
  data?: SyncResponResponse;
}

/**
 * Sync Respon: fetch CEISA status, persist riwayat respon, and send emails.
 * POST /api/v1/ceisa/uploads/sync-respon
 * Body: { nomorAju }
 */
export default function useSyncRespon() {
  const queryClient = useQueryClient();
  const [loadingAju, setLoadingAju] = useState<string | null>(null);

  const syncRespon = async (nomorAju: string) => {
    const trimmedAju = (nomorAju || "").trim();
    if (!trimmedAju) {
      notifications.show({
        title: "Error",
        message: "Nomor AJU tidak tersedia",
        color: "red",
      });
      return null;
    }

    setLoadingAju(trimmedAju);
    try {
      const { data } = await axiosInstance.post<ApiEnvelope>(
        "/api/v1/ceisa/uploads/sync-respon",
        { nomorAju: trimmedAju }
      );

      const payload = data?.data;
      const latestLabel = payload?.latestRespon?.trim();
      const emailsSent = payload?.emailsSent ?? 0;
      const savedCount = payload?.savedCount ?? 0;
      const errors = payload?.errors ?? [];

      const message =
        payload?.message ||
        data?.message ||
        (latestLabel
          ? `Respon tersinkron: ${latestLabel}`
          : "Status respon berhasil diambil");

      const hasHardError = errors.length > 0 && emailsSent === 0 && savedCount === 0;
      notifications.show({
        title: hasHardError ? "Sync selesai dengan error" : "Success",
        message:
          emailsSent > 0
            ? `${message} (${emailsSent} email)`
            : message,
        color: hasHardError ? "yellow" : "green",
      });

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["doc-pabeans"] });
      }, 600);

      return payload ?? null;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Gagal sync respon";
      notifications.show({ title: "Error", message: errorMessage, color: "red" });
      return null;
    } finally {
      setLoadingAju(null);
    }
  };

  return {
    syncRespon,
    loadingAju,
    loading: loadingAju !== null,
  };
}
